import React, { useState, useRef, useEffect } from "react";
import { Button, message } from "antd";
import { CameraOutlined, CloseCircleOutlined, SwapOutlined } from '@ant-design/icons';
import { Camera } from "react-camera-pro";
import axios from '../../../config/axiosInstance';

const FullScreenCamera = ({ onCancel, host, participant }) => {
    const camera = useRef(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [isCameraActive, setIsCameraActive] = useState(true);
    const [cameraError, setCameraError] = useState(false);
    const [facingMode, setFacingMode] = useState("environment"); // 🔄 frontal o trasera

    useEffect(() => {
        const checkCameraAvailability = async () => {
            try {
                await navigator.mediaDevices.getUserMedia({ video: true });
                setCameraError(false);
            } catch (error) {
                setCameraError(true);
            }
        };
        checkCameraAvailability();
    }, []);

    const capturePhoto = () => {
        if (camera.current) {
            const photo = camera.current.takePhoto();
            setCapturedImage(photo);
            setIsCameraActive(false);
            message.success('Photo captured!');
        } else {
            message.error('No camera available!');
        }
    };

    const handleUpload = async () => {
        if (!capturedImage) return message.error('No photo captured');
        try {
            if (!host) return message.error('Host is missing!');

            const tokenRes = await axios.get(`/regasus/token?host=${host}`);
            const token = tokenRes.data.access_token;
            if (!token) return message.error('No token received');

            const fileName = `photo_${participant?.firstName}_${participant?.lastName}.png`;

            await axios.post(`/regasus/participants/${participant?.key}/files-base64`, {
                token,
                host,
                name: fileName,
                description: 'Photo uploaded from Dsync camera',
                language: 'en',
                file: capturedImage,
            });

            message.success('Photo uploaded successfully!');
            onCancel();
        } catch (error) {
            console.error('Upload error:', error);
            message.error('Failed to upload photo');
        }
    };

    const handleDiscard = () => {
        setCapturedImage(null);
        setIsCameraActive(true);
    };

    const toggleFacingMode = () => {
        setFacingMode(prev => (prev === 'environment' ? 'user' : 'environment'));
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            zIndex: 9998,
        }}>
            {isCameraActive && !cameraError && (
                <Camera
                    key={facingMode} // 🔁 Esto fuerza el re-mount
                    ref={camera}
                    facingMode={facingMode}
                    onError={() => setCameraError(true)}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
            )}

            {capturedImage && (
                <div style={{
                    position: 'absolute', top: 0, left: 0,
                    width: '100%', height: '100%',
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                }}>
                    <img src={capturedImage} alt="Captured"
                         style={{ maxWidth: '100%', maxHeight: '80%', objectFit: 'contain', zIndex: 1 }}
                    />
                </div>
            )}

            {cameraError && (
                <div style={{
                    position: 'absolute', zIndex: 10000,
                    backgroundColor: '#ffccc7', color: 'black',
                    fontSize: '18px', fontWeight: 'bold', padding: '20px',
                    borderRadius: '8px', textAlign: 'center',
                    maxWidth: '80%', border: '2px solid #f44336',
                }}>
                    <CloseCircleOutlined style={{ marginRight: '5px' }} />
                    No camera device accessible. Please connect your camera or try a different browser.
                </div>
            )}

            {/* Button: Take Photo - centered below*/}
            <Button
                onClick={capturePhoto}
                style={{
                    position: 'fixed',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#333',
                    borderRadius: '50%',
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 0,
                    zIndex: 10,
                }}
            >
                <CameraOutlined style={{ fontSize: '36px', color: '#fff' }} />
            </Button>

            {/* Button: Flip Camera - to the right of the previous button*/}
            <Button
                onClick={() => setFacingMode(prev => prev === "user" ? "environment" : "user")}
                style={{
                    position: 'fixed',
                    bottom: '30px',
                    left: 'calc(50% + 70px)',
                    backgroundColor: '#333',
                    borderRadius: '50%',
                    width: '60px',
                    height: '60px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 0,
                    zIndex: 10,
                }}
            >
                <SwapOutlined style={{ fontSize: '24px', color: '#fff' }} />
            </Button>

            {/* Upload / Discard */}
            {capturedImage && (
                <div style={{
                    position: 'absolute', bottom: '100px', zIndex: 10,
                    display: 'flex', justifyContent: 'center',
                    width: '100%', padding: '0 20px',
                }}>
                    <Button onClick={handleUpload} type="primary" style={{ marginRight: '10px', width: '200px' }}>
                        Upload Photo
                    </Button>
                    <Button onClick={handleDiscard} style={{ width: '200px' }}>
                        Discard
                    </Button>
                </div>
            )}

            <Button
                onClick={onCancel}
                type="link"
                style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    color: 'white',
                    zIndex: 10,
                }}
            >
                Close
            </Button>
        </div>
    );
};

export default FullScreenCamera;